import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  LANGUAGES,
  PAGE_LANGUAGE,
  getCurrentLang,
  setGoogTransCookie,
  clearGoogTransCookie,
  isRtlLang,
  applyDirection,
} from "../utils/googleTranslate";
import {
  CONTENT_LANGUAGE_AUTO,
  clearLanguageDependentCaches,
  getContentLanguage,
  getContentLanguageSetting,
} from "../utils/contentLanguage";
import { setUiLanguage } from "../utils/uiTranslator";

/**
 * Site-wide language switcher powered by the Google Website Translator.
 *
 * - The Google script is loaded once globally (see utils/googleTranslate.ts,
 *   initialised in index.tsx), so it works on every screen and page load.
 * - Google's own dropdown is hidden; we drive it from this custom MUI menu.
 * - The selected language is stored in the `googtrans` cookie (root domain),
 *   so it persists across refreshes, routes and subdomains.
 * - Add className="notranslate" (or translate="no") to anything that must stay
 *   as-is (brand names, user-entered data, code, etc).
 * - The banner-hiding CSS lives in the global stylesheet (index.css).
 */

// "zh-CN" -> "ZH", "ta" -> "TA"
// const shortCode = (code: string) => code.split("-")[0].toUpperCase();
const shortCode = (code: string) =>
  LANGUAGES.find((l) => l.code === code)?.short ?? code.split("-")[0].toUpperCase();

const LanguageSwitcher: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [current, setCurrent] = useState<string>(PAGE_LANGUAGE);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setCurrent(getCurrentLang());

    // Safety net: remove Google's banner / top offset if it slips through
    const observer = new MutationObserver(() => {
      document
        .querySelectorAll("iframe.skiptranslate, .goog-te-banner-frame")
        .forEach((el) => ((el as HTMLElement).style.display = "none"));
      if (document.body.style.top && document.body.style.top !== "0px") {
        document.body.style.top = "0px";
      }
    });
    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ["style"],
      subtree: false,
    });

    return () => observer.disconnect();
  }, []);

  const changeLanguage = (code: string) => {
    setAnchorEl(null);
    if (code === current) return;
    // Content follows the site language: suggestions cached in the old language are dropped
    if (getContentLanguageSetting() === CONTENT_LANGUAGE_AUTO) clearLanguageDependentCaches();

    const directionChanges = isRtlLang(code) !== isRtlLang(current);

    // Saved in the cookie (shared across routes / subdomains), applied live by our
    // own translator using the translations stored in the database.
    if (code === PAGE_LANGUAGE) clearGoogTransCookie();
    else setGoogTransCookie(code);
    applyDirection(code);
    setCurrent(code);

    // LTR <-> RTL switch: reload so the whole app (incl. MUI) re-renders in the new direction
    if (directionChanges) {
      window.location.reload();
      return;
    }
    setUiLanguage(code);
    window.dispatchEvent(new CustomEvent("site-language-changed", { detail: code }));
    // Content follows the site language: tell open pages (dashboard suggestions) to regenerate
    if (getContentLanguageSetting() === CONTENT_LANGUAGE_AUTO) {
      window.dispatchEvent(new CustomEvent("content-language-changed", { detail: getContentLanguage() }));
    }
  };

  return (
    <>
      <Tooltip title="Change language">
        <ButtonBase
          onClick={(e) => setAnchorEl(e.currentTarget)}
          className="notranslate"
          translate="no"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            height: 34,
            pl: 1.2,
            pr: 0.8,
            borderRadius: "999px",
            color: "rgba(255,255,255,0.85)",
            background: open ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.06)",
            border: "1px solid",
            borderColor: open ? "rgba(167,139,250,0.6)" : "rgba(255,255,255,0.12)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
              borderColor: "rgba(255,255,255,0.25)",
            },
          }}
        >
          <LanguageIcon sx={{ fontSize: 18, color: open ? "#A78BFA" : "inherit" }} />
          <Typography
            component="span"
            sx={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", lineHeight: 1 }}
          >
            {shortCode(current)}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              opacity: 0.7,
              transition: "transform 0.2s ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // notranslate so language names always stay in their own script
        slotProps={{
          paper: {
            className: "notranslate",
            sx: {
              mt: 1,
              minWidth: 210,
              maxHeight: 360,
              p: 0.5,
              borderRadius: "14px",
              background: "rgba(24,24,30,0.96)",
              backdropFilter: "blur(12px)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
            },
          },
        }}
      >
        {LANGUAGES.map((l) => {
          const selected = l.code === current;
          return (
            <MenuItem
              key={l.code}
              selected={selected}
              onClick={() => changeLanguage(l.code)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderRadius: "10px",
                py: 0.9,
                "&.Mui-selected": { background: "rgba(139,92,246,0.18)" },
                "&.Mui-selected:hover": { background: "rgba(139,92,246,0.26)" },
                "&:hover": { background: "rgba(255,255,255,0.08)" },
              }}
            >
              <Box
                sx={{
                  minWidth: 32,
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  py: 0.3,
                  borderRadius: "6px",
                  color: selected ? "#C4B5FD" : "rgba(255,255,255,0.6)",
                  background: selected ? "rgba(139,92,246,0.25)" : "rgba(255,255,255,0.08)",
                }}
              >
                {shortCode(l.code)}
              </Box>
              <Typography sx={{ fontSize: 14, flexGrow: 1 }}>{l.label}</Typography>
              {selected && <CheckIcon sx={{ fontSize: 16, color: "#A78BFA" }} />}
            </MenuItem>
          );
        })}

      </Menu>
    </>
  );
};

export default LanguageSwitcher;